import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  bottleMilkTypeForEvent,
  fetchBabyEvents,
  normalizeFeedingType,
} from "./BabyTrackerShared";

const windowHours = 24;
const windowMs = windowHours * 60 * 60 * 1000;

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "No data";
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
};

const formatTime = (date) => date.toLocaleTimeString([], {
  hour: "numeric",
  minute: "2-digit",
});

const feedingDescription = (entry) => {
  const feedingType = normalizeFeedingType(entry.eventType);
  if (feedingType === "Bottle") {
    const amount = entry.feedingAmount ? `${entry.feedingAmount} ${entry.feedingUnit}` : "Bottle";
    return `${amount} ${bottleMilkTypeForEvent(entry)}`;
  }
  const amount = entry.feedingAmount ? `${entry.feedingAmount} ${entry.feedingUnit}` : "Breast";
  return entry.feedingSide ? `${amount}, started ${entry.feedingSide}` : amount;
};

const BabyTrackerFeedingChart = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const capturedNow = new Date();
    setNow(capturedNow);
    fetchBabyEvents("feeding", 200)
      .then((data) => {
        setEvents(data || []);
        setLoading(false);
      })
      .catch(() => {
        setEvents([]);
        setLoading(false);
      });
  }, []);

  const chartData = useMemo(() => {
    const windowStart = new Date(now.getTime() - windowMs);
    const feedings = events
      .map((entry) => ({
        ...entry,
        occurredDate: new Date(entry.occurredAtInput),
      }))
      .filter((entry) => (
        !Number.isNaN(entry.occurredDate.getTime())
        && entry.occurredDate >= windowStart
        && entry.occurredDate <= now
      ))
      .sort((left, right) => left.occurredDate - right.occurredDate);

    const feedingsWithIntervals = feedings.map((entry, index) => {
      const previous = feedings[index - 1];
      return {
        ...entry,
        minutesSincePrevious: previous
          ? Math.round((entry.occurredDate - previous.occurredDate) / 60000)
          : null,
        chartPosition: ((entry.occurredDate - windowStart) / windowMs) * 100,
      };
    });

    const lastFeeding = feedingsWithIntervals[feedingsWithIntervals.length - 1] || null;
    const currentGapMinutes = lastFeeding
      ? Math.round((now - lastFeeding.occurredDate) / 60000)
      : null;
    const completedIntervals = feedingsWithIntervals
      .map((entry) => entry.minutesSincePrevious)
      .filter((minutes) => minutes !== null);
    const longestGapMinutes = completedIntervals.length ? Math.max(...completedIntervals) : null;

    return {
      windowStart,
      feedings: feedingsWithIntervals,
      lastFeeding,
      currentGapMinutes,
      longestGapMinutes,
    };
  }, [events, now]);

  const axisLabels = [
    chartData.windowStart,
    new Date(chartData.windowStart.getTime() + 6 * 60 * 60 * 1000),
    new Date(chartData.windowStart.getTime() + 12 * 60 * 60 * 1000),
    new Date(chartData.windowStart.getTime() + 18 * 60 * 60 * 1000),
    now,
  ];

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">Feeding timeline</h2>
          <p className="BabyTrackerSubtitle">
            Feedings from the last 24 hours and the open interval since the last feed.
          </p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Planning window</h3>
        <div className="BabyTrackerMetricGrid">
          <div className="BabyTrackerMetric">
            <span>Since last feed</span>
            <strong>{formatDuration(chartData.currentGapMinutes)}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Last fed</span>
            <strong>{chartData.lastFeeding ? formatTime(chartData.lastFeeding.occurredDate) : "None"}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Feeds</span>
            <strong>{chartData.feedings.length}</strong>
          </div>
          <div className="BabyTrackerMetric">
            <span>Longest gap</span>
            <strong>{formatDuration(chartData.longestGapMinutes)}</strong>
          </div>
        </div>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Last 24 hours</h3>
        {loading ? <p>Loading feeding timeline...</p> : null}
        {!loading && !chartData.feedings.length ? <p>No feedings logged in the last 24 hours.</p> : null}
        {chartData.feedings.length ? (
          <div className="BabyTrackerTimeline" aria-label="Feeding timeline over the last 24 hours">
            <div className="BabyTrackerTimelineTrack"></div>
            {chartData.feedings.map((entry, index) => (
              <div
                className="BabyTrackerTimelineMarker"
                key={entry.id}
                style={{
                  left: `${entry.chartPosition}%`,
                  top: `${32 + (index % 3) * 18}px`,
                }}
                title={`${formatTime(entry.occurredDate)} - ${feedingDescription(entry)}`}
              >
                <span className="BabyTrackerTimelineDot"></span>
                <span className="BabyTrackerTimelineLabel">{formatTime(entry.occurredDate)}</span>
              </div>
            ))}
            <div className="BabyTrackerTimelineAxis">
              {axisLabels.map((label) => (
                <span key={label.toISOString()}>{formatTime(label)}</span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <h3>Feeding intervals</h3>
        {chartData.feedings.length ? (
          [...chartData.feedings].reverse().map((entry) => (
            <div className="BabyTrackerHistoryRow" key={entry.id}>
              <div>
                <strong>{formatTime(entry.occurredDate)}</strong>
                <div>{feedingDescription(entry)}</div>
              </div>
              <span>
                {entry.minutesSincePrevious === null
                  ? "First in window"
                  : `${formatDuration(entry.minutesSincePrevious)} after previous`}
              </span>
            </div>
          ))
        ) : (
          <p>No intervals to show yet.</p>
        )}
      </div>
    </div>
  );
};

export default BabyTrackerFeedingChart;
